import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, onMounted, ref, watch } from 'vue';
import { useContext } from '@nuxtjs/composition-api';
import { useIntersectionObserver } from '@vueuse/core';
import { formatDistanceToNow } from 'date-fns';
import AvatarIcon from '~/components/UIComponents/AvatarIcon.vue';
import {
  useChat,
  getNumericTimetoken,
  isFullUserData,
} from '~/composables/useChat';
import { IEnvelope, Timetoken } from '~/types/chat';
import { getDateFormatsFromLocale, SupportedLocales } from '~/utils/locales';
import MessageMenu from './MessageMenu.vue';
import MessageReactions from './MessageReactions.vue';

// gross but this is workaround for a bug with importing types/interfaces for defineProps
// @see https://github.com/vuejs/core/issues/4294

export default /*#__PURE__*/_defineComponent({
  __name: 'Message',
  props: {
    actions: null,
    message: null,
    timetoken: null,
    uuid: null
  },
  emits: ["mounted", "intersected", "deleted", "undeleted", "edited"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'mounted', timetoken: Timetoken): void;
  (
    e: 'intersected',
    {
      timetoken,
      intersectionRatio,
    }: { timetoken: Timetoken; intersectionRatio: number },
  ): void;
  (e: 'deleted', timetoken: Timetoken): void;
  (e: 'undeleted', timetoken: Timetoken): void;
  (e: 'edited', timetoken: Timetoken): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  actions?: IEnvelope['actions'];
  message: IEnvelope['message'];
  timetoken: IEnvelope['timetoken'];
  uuid?: IEnvelope['uuid'];
};





const { pubnub, addMember, isUserBanned, members } = useChat();
const { i18n } = useContext();

const authorData = computed(() => {
  const member = members.value?.[props.uuid ?? 0];
  return member?.data?.uuid && isFullUserData(member.data?.uuid)
    ? member.data.uuid
    : {
        id: member?.data?.uuid?.id,
        name: '',
        profileUrl: '',
      };
});
const isLoadingMetadata = computed(
  () => members.value?.[props?.uuid || 0]?.loading,
);

const messageRef = ref<HTMLDivElement | null>(null);
const scrollParent = computed(() =>
  messageRef.value?.closest<HTMLUListElement>('ul.messages'),
);
const scrollOffset = 16;

const date = computed(() => new Date(Math.round(+props.timetoken / 10000)));
const isCurrentUser = computed(
  () => !!props.uuid && props.uuid === pubnub.getUUID(),
);
const isDeleted = computed(
  () => !!Object.keys(props.actions?.deleted || {}).length,
);

const displayDate = computed(() =>
  date.value?.getTime() >= Date.now()
    ? `now`
    : formatDistanceToNow(date.value, {
        addSuffix: true,
        includeSeconds: true,
        locale: getDateFormatsFromLocale(i18n.locale as SupportedLocales),
      }),
);

const messageText = computed(() => {
  const edits = props.actions?.hasOwnProperty('updated')
    ? Object.entries(props.actions?.updated || {})
    : [];
  if (!edits.length) {
    return props.message.text;
  }
  if (edits.length === 1) {
    return edits[0][0];
  }
  // if we have more than one edit, we have to sort them by actionTimetoken
  const sortedEdits = edits.sort((a, b) => {
    const aActionTimetoken = getNumericTimetoken(
      a[1][a[1].length - 1].actionTimetoken ?? 0,
    );
    const bActionTimetoken = getNumericTimetoken(
      b[1][b[1].length - 1].actionTimetoken ?? 0,
    );
    return aActionTimetoken < bActionTimetoken
      ? -1
      : aActionTimetoken > bActionTimetoken
      ? 1
      : 0;
  });
  return sortedEdits.pop()?.[0];
});

const { stop } = useIntersectionObserver(
  messageRef,
  ([{ isIntersecting, intersectionRatio }]) => {
    if (isIntersecting) {
      stop();
      emit('intersected', { timetoken: props.timetoken, intersectionRatio });
    }
  },
  {
    root: scrollParent,
    rootMargin: `0px 0px -${scrollOffset}px 0px`,
    threshold: 0,
  },
);

watch(isDeleted, (current, previous) => {
  if (current === previous) {
    return;
  }
  if (current) {
    emit('deleted', props.timetoken);
  } else {
    emit('undeleted', props.timetoken);
  }
});

watch(messageText, (current, previous) => {
  if (current === previous) {
    return;
  }
  emit('edited', props.timetoken);
});

onBeforeMount(async () => {
  if (!props.uuid) {
    return;
  }

  if (authorData.value?.id || isLoadingMetadata.value) {
    return;
  }

  await addMember(props.uuid);
});

onMounted(() => {
  emit('mounted', props.timetoken);
});

return { __sfc: true,props, emit, pubnub, addMember, isUserBanned, members, i18n, authorData, isLoadingMetadata, messageRef, scrollParent, scrollOffset, date, isCurrentUser, isDeleted, displayDate, messageText, stop, AvatarIcon, MessageMenu, MessageReactions }
}

})